import React from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';
import { ChangeNodeStyleCommand } from '../../core/commands/ChangeNodeStyleCommand';
import { CompositeCommand } from '../../core/commands/ICommand';
import { UpdateTableCellStylesCommand } from '../../core/commands/UpdateTableCellStylesCommand';
import { UpdateImageNodeAttrsCommand, type ImageNodeUpdate } from '../../core/commands/UpdateImageNodeAttrsCommand';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Separator } from '../../components/ui/separator';
import { Label } from '../../components/ui/label';
import { useThemeMode } from '../../react-app/theme/ThemeProvider';
import { generateDarkModeColorPalette } from '../../react-app/theme/color-utils';
import { TOOL_APPLICABILITY } from '../../core/tools/config/toolApplicability';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Button } from '../../components/ui/button';
import { Check, Pipette, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '../../hooks/useTranslation';
import { useTableCellStore, type TableCellKey } from '../../stores/tableCellStore';
import { SessionMetrics } from '../../domains/analytics/services/SessionMetrics';

const RECENT_CUSTOM_COLORS_KEY = 'outline-toolbar-recent-custom-colors';
const HEX_FULL_REGEX = /^#[0-9A-F]{6}$/;
const HEX_SHORT_REGEX = /^#[0-9A-F]{3}$/;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const expandHex = (hex: string) => {
  const normalized = hex.trim().toUpperCase();
  if (HEX_FULL_REGEX.test(normalized)) return normalized;
  if (HEX_SHORT_REGEX.test(normalized)) {
    const r = normalized[1];
    const g = normalized[2];
    const b = normalized[3];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return '#3662E3';
};

const hexToRgb = (hex: string) => {
  const expanded = expandHex(hex).slice(1);
  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);
  return { r, g, b };
};

const rgbToHex = (r: number, g: number, b: number) => {
  const toHex = (component: number) =>
    clamp(Math.round(component), 0, 255).toString(16).padStart(2, '0').toUpperCase();
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const rgbToHsv = (r: number, g: number, b: number) => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === rn) {
      h = 60 * (((gn - bn) / delta) % 6);
    } else if (max === gn) {
      h = 60 * ((bn - rn) / delta + 2);
    } else {
      h = 60 * ((rn - gn) / delta + 4);
    }
  }
  if (h < 0) h += 360;

  const s = max === 0 ? 0 : delta / max;
  const v = max;
  return { h, s, v };
};

const hsvToRgb = (h: number, s: number, v: number) => {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let rp = 0;
  let gp = 0;
  let bp = 0;

  if (h >= 0 && h < 60) {
    rp = c;
    gp = x;
  } else if (h >= 60 && h < 120) {
    rp = x;
    gp = c;
  } else if (h >= 120 && h < 180) {
    gp = c;
    bp = x;
  } else if (h >= 180 && h < 240) {
    gp = x;
    bp = c;
  } else if (h >= 240 && h < 300) {
    rp = x;
    bp = c;
  } else {
    rp = c;
    bp = x;
  }

  return {
    r: (rp + m) * 255,
    g: (gp + m) * 255,
    b: (bp + m) * 255
  };
};

const hsvToHex = (h: number, s: number, v: number) => {
  const { r, g, b } = hsvToRgb(h, s, v);
  return rgbToHex(r, g, b);
};

const isOrgChartNode = (context: IToolContext, nodeId: string): boolean => {
  const shape = context.shapes.get(nodeId) as any;
  const data = shape?.data ?? shape;
  const category = data?.category ?? data?.attrs?.category;
  const type = data?.type as string | undefined;
  return category === 'orgchart' || (type ? type.startsWith('org_') : false);
};

const hasOrgChartSelection = (context: IToolContext): boolean => {
  const selectedIds = Array.from(context.selection);
  if (selectedIds.length === 0) return false;
  return selectedIds.some((id) => isOrgChartNode(context, id));
};

const isImageNode = (context: IToolContext, nodeId: string): boolean => {
  const shape = context.shapes.get(nodeId) as any;
  const data = shape?.data ?? shape;
  return data?.type === 'image';
};

const normalizeHexColor = (value: string): string | null => {
  if (!value) return null;
  const trimmed = value.trim().toUpperCase();
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;

  if (HEX_FULL_REGEX.test(withHash)) {
    return withHash;
  }

  if (HEX_SHORT_REGEX.test(withHash)) {
    const r = withHash[1];
    const g = withHash[2];
    const b = withHash[3];
    return `#${r}${r}${g}${g}${b}${b}`;
  }

  return null;
};

const isTransparentColor = (value?: string | null) => (value ?? '').toLowerCase() === 'transparent';

const getOrgShapeBorderColor = (context: IToolContext): string | null => {
  const selectedIds = Array.from(context.selection);
  if (selectedIds.length === 0) return null;
  for (const id of selectedIds) {
    const shape = context.shapes.get(id) as any;
    const data = shape?.data ?? shape;
    const category = data?.category ?? data?.attrs?.category;
    const type = data?.type as string | undefined;
    if (category !== 'orgchart' && !(type ? type.startsWith('org_') : false)) {
      continue;
    }
    const attrs = (data?.attrs as Record<string, unknown>) ?? {};
    const props = (data?.props as Record<string, unknown>) ?? {};
    const stroke = (data?.strokeColor as string) || (attrs.strokeColor as string) || (attrs.borderColor as string) || (props.borderColor as string);
    return stroke || '#05DF72';
  }
  return null;
};

const resolveSelectedTableCellBorder = (context: IToolContext): { color?: string; width?: number; dash?: number[] } | null => {
  const nodeIds = Array.from(context.selection);
  if (nodeIds.length === 0) return null;

  const selections = useTableCellStore.getState().selections;

  for (const nodeId of nodeIds) {
    const sel = selections[nodeId];
    const keys = sel?.selectedKeys ?? [];
    if (keys.length === 0) continue;

    const shape = context.shapes.get(nodeId) as any;
    const node = shape?.data ?? shape;
    const attrs = (node?.attrs ?? node?.data?.attrs ?? {}) as Record<string, unknown>;
    const cellStyles = (attrs.cellStyles as Record<TableCellKey, { borderColor?: string; borderWidth?: number; borderDash?: number[] }> | undefined) ?? {};
    const key = (sel?.anchorKey ?? keys[0]) as TableCellKey;
    const style = cellStyles[key];
    if (!style) return null;
    return { color: style.borderColor, width: style.borderWidth, dash: style.borderDash };
  }
  return null;
};

type CanvasImageOutlineLens = {
  strokeWidth: number;
  strokeColor?: string;
  dash: number[];
  dashEnabled: boolean;
};

const getCanvasImageOnlyOutlineLens = (context: IToolContext): CanvasImageOutlineLens | null => {
  const ids = Array.from(context.selection);
  if (ids.length === 0) return null;
  for (const id of ids) {
    if (!isImageNode(context, id)) return null;
  }
  const shape = context.shapes.get(ids[0]) as { data?: Record<string, unknown> } | undefined;
  const data = (shape?.data ?? shape) as Record<string, unknown> | undefined;
  if (!data) return null;
  const attrs = (data.attrs as Record<string, unknown>) ?? {};
  const bw = attrs.borderWidth;
  const strokeWidth =
    typeof bw === 'number' && Number.isFinite(bw)
      ? bw
      : typeof data.strokeWidth === 'number' && Number.isFinite(data.strokeWidth)
        ? (data.strokeWidth as number)
        : typeof attrs.strokeWidth === 'number'
          ? (attrs.strokeWidth as number)
          : 1;
  const borderDash = Array.isArray(attrs.borderDash)
    ? (attrs.borderDash as number[])
    : Array.isArray(attrs.dash)
      ? (attrs.dash as number[])
      : [];
  const dashEnabled = Boolean(
    (typeof attrs.dashEnabled === 'boolean' ? attrs.dashEnabled : borderDash.length > 0) &&
      borderDash.length > 0,
  );
  let strokeColor: string | undefined;
  if (typeof attrs.borderColor === 'string') strokeColor = attrs.borderColor;
  else if (typeof data.strokeColor === 'string') strokeColor = data.strokeColor;
  else if (typeof attrs.strokeColor === 'string') strokeColor = attrs.strokeColor;

  return { strokeWidth, strokeColor, dash: borderDash, dashEnabled };
};

const strokeStyles = [
  { value: 'solid', label: 'Solid', dash: [] },
  { value: 'dashed', label: 'Dashed', dash: [8, 4] },
  { value: 'dotted', label: 'Dotted', dash: [2, 4] },
  { value: 'dash-dot', label: 'Dash Dot', dash: [8, 4, 2, 4] },
];

const outlinePresetColors = [
  // First row - light warm colors
  ['transparent', '#F3CE49', '#F2C14B', '#E9A23B'],
  // Second row - light cool to warm
  ['#4BA154', '#3982C2', '#4E46DC', '#CC7B2E'],
  // Third row - mid warm to cool
  ['#8E3B1E', '#8C2822', '#306339', '#263FA9'],
  // Fourth row - mid cool to warm  
  ['#5425AF', '#6A411C', '#C93B76', '#CA3A31'],
  // Fifth row - cool dark
  ['#214D3C', '#253985', '#302E7C', '#672171'],
  // Sixth row - gray
  ['#E3E8EF', '#97A3B6', '#4D5562', '#000000'],
];

const orgPresetColors = [
  // First row - light warm colors
  ['transparent', '#FCF9C9', '#F3BD7F', '#F6CCCB'],
  // Second row - light cool to warm
  ['#C7F5D3', '#C3E5FA', '#C9D2FA', '#F5D565'],
  // Third row - mid warm to cool
  ['#ED974F', '#E77975', '#77DB89', '#70A3F3'],
  // Fourth row - mid cool to warm  
  ['#A38CF3', '#C18D30', '#70391A', '#CA3A31'],
  // Fifth row - cool dark
  ['#43946C', '#3662E3', '#652CD1', '#FFFFFF'],
  // Sixth row - gray
  ['#E3E8EF', '#97A3B6', '#4A5567', '#000000'],
];

interface AdvancedColorPickerProps {
  color: string;
  onColorChange: (hex: string) => void;
}

const AdvancedColorPicker: React.FC<AdvancedColorPickerProps> = ({ color, onColorChange }) => {
  const [{ h, s, v }, setHsv] = React.useState(() => {
    const { r, g, b } = hexToRgb(color);
    return rgbToHsv(r, g, b);
  });

  React.useEffect(() => {
    if (!HEX_FULL_REGEX.test(color) && !HEX_SHORT_REGEX.test(color)) {
      return;
    }
    const { r, g, b } = hexToRgb(color);
    const next = rgbToHsv(r, g, b);
    setHsv(next);
  }, [color]);

  const updateColor = React.useCallback(
    (next: { h?: number; s?: number; v?: number }) => {
      setHsv((prev) => {
        const current = {
          h: next.h !== undefined ? next.h : prev.h,
          s: next.s !== undefined ? next.s : prev.s,
          v: next.v !== undefined ? next.v : prev.v
        };
        const hex = hsvToHex(current.h, current.s, current.v);
        onColorChange(hex);
        return current;
      });
    },
    [onColorChange]
  );

  const handleSaturationValueEvent = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      const target = event.currentTarget;
      const rect = target.getBoundingClientRect();
      const updateFromPointer = (clientX: number, clientY: number) => {
        const saturation = clamp((clientX - rect.left) / rect.width, 0, 1);
        const value = clamp(1 - (clientY - rect.top) / rect.height, 0, 1);
        updateColor({ s: saturation, v: value });
      };
      const handlePointerMove = (moveEvent: PointerEvent) => {
        updateFromPointer(moveEvent.clientX, moveEvent.clientY);
      };
      const handlePointerUp = () => {
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
      };

      updateFromPointer(event.clientX, event.clientY);
      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
    },
    [updateColor]
  );

  const handleHueEvent = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      const target = event.currentTarget;
      const rect = target.getBoundingClientRect();
      const updateFromPointer = (clientY: number) => {
        const ratio = clamp((clientY - rect.top) / rect.height, 0, 1);
        const hue = ratio * 360;
        updateColor({ h: hue });
      };
      const handlePointerMove = (moveEvent: PointerEvent) => {
        updateFromPointer(moveEvent.clientY);
      };
      const handlePointerUp = () => {
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
      };

      updateFromPointer(event.clientY);
      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
    },
    [updateColor]
  );

  const hueColor = hsvToHex(h, 1, 1);

  return (
    <div className="flex gap-3">
      <div
        className="relative h-[160px] w-[216px] cursor-crosshair rounded-md"
        style={{
          backgroundImage: `
            linear-gradient(0deg, #000000, rgba(0,0,0,0)),
            linear-gradient(90deg, #ffffff, ${hueColor})
          `
        }}
        onPointerDown={handleSaturationValueEvent}
        role="presentation"
        aria-label="Pick saturation and brightness"
      >
        <div
          className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.3)]"
          style={{
            left: `${s * 100}%`,
            top: `${(1 - v) * 100}%`
          }}
        />
      </div>
      <div
        className="relative h-[160px] w-6 cursor-pointer rounded-md"
        style={{
          background: 'linear-gradient(180deg, #ff0000 0%, #ffff00 16.6%, #00ff00 33.3%, #00ffff 50%, #0000ff 66.6%, #ff00ff 83.3%, #ff0000 100%)'
        }}
        onPointerDown={handleHueEvent}
        role="presentation"
        aria-label="Pick hue"
      >
        <div
          className="pointer-events-none absolute left-1/2 h-2 w-6 -translate-x-1/2 rounded-sm border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.3)]"
          style={{ top: `${(h / 360) * 100}%` }}
        />
      </div>
    </div>
  );
};

// Infer a canonical stroke style from a dash array found on selection
const resolveStrokeStyleFromDash = (dash?: number[] | null, dashEnabled?: boolean): 'solid' | 'dashed' | 'dotted' | 'dash-dot' => {
  if (!dashEnabled || !dash || dash.length === 0) return 'solid';

  // Helper: loose comparison with small tolerance
  const approxEqual = (a: number, b: number, tol = 1) => Math.abs(a - b) <= tol;
  const match2 = (a: number, b: number, A: number, B: number) => approxEqual(a, A) && approxEqual(b, B);

  // Normalize to first four numbers for safety
  const d = dash.slice(0, 4);

  // Known patterns (accept common renderer variants)
  if (d.length === 2) {
    const [a, b] = d;
    // Dotted ~ [2,4] (allow small variance and [1,2])
    if (
      match2(a, b, 2, 4) ||
      match2(a, b, 1, 2) ||
      (a <= 3 && b >= 3 && a / b <= 0.75)
    ) {
      return 'dotted';
    }
    // Dashed ~ [8,4] or [10,5]
    if (
      match2(a, b, 8, 4) ||
      match2(a, b, 10, 5) ||
      (a >= 6 && b >= 3 && a / b >= 1.5)
    ) {
      return 'dashed';
    }
    // Fallback to dashed for any 2-length pattern
    return 'dashed';
  }

  if (d.length === 4) {
    const [a, b, c, e] = d;
    // Dash-dot ~ [8,4,2,4] (allow small variance)
    if (
      (approxEqual(a, 8, 2) || approxEqual(a, 10, 2)) &&
      approxEqual(b, 4, 1) &&
      approxEqual(c, 2, 1) &&
      approxEqual(e, 4, 1)
    ) {
      return 'dash-dot';
    }
    // If first segment is much larger than third, treat as dash-dot
    if (a > 2 * (c || 1)) return 'dash-dot';
    return 'dashed';
  }

  // Any other pattern: treat as dashed
  return 'dashed';
};

const ShapeOutlineToolComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const [open, setOpen] = React.useState(false);
  const cellBorder = React.useMemo(() => resolveSelectedTableCellBorder(context), [context]);
  const imageOutlineLens = React.useMemo(() => getCanvasImageOnlyOutlineLens(context), [context]);
  const [strokeWidth, setStrokeWidth] = React.useState(
    cellBorder?.width ?? imageOutlineLens?.strokeWidth ?? context.selectionStyles?.strokeWidth ?? 2,
  );
  const [strokeStyle, setStrokeStyle] = React.useState(
    resolveStrokeStyleFromDash(
      cellBorder?.dash ?? imageOutlineLens?.dash ?? context.selectionStyles?.dash,
      (cellBorder?.dash?.length ?? 0) > 0 ||
        imageOutlineLens?.dashEnabled ||
        context.selectionStyles?.dashEnabled,
    ),
  );
  const isOrgSelection = React.useMemo(() => hasOrgChartSelection(context), [context]);

  const initialStrokeColor = React.useMemo(() => {
    const orgBorder = isOrgSelection ? getOrgShapeBorderColor(context) : null;
    const incoming =
      cellBorder?.color ?? orgBorder ?? imageOutlineLens?.strokeColor ?? context.selectionStyles?.strokeColor;
    if (isTransparentColor(incoming)) return 'transparent';
    return normalizeHexColor(incoming ?? '#475569') ?? '#475569';
  }, [cellBorder?.color, context.selectionStyles?.strokeColor, context, isOrgSelection, imageOutlineLens?.strokeColor]);

  const [strokeColor, setStrokeColor] = React.useState(initialStrokeColor);
  const [customStrokeColor, setCustomStrokeColor] = React.useState(
    normalizeHexColor(initialStrokeColor) ?? '#475569'
  );
  const [customStrokeColorDraft, setCustomStrokeColorDraft] = React.useState(
    normalizeHexColor(initialStrokeColor) ?? '#475569'
  );
  const [recentCustomColors, setRecentCustomColors] = React.useState<string[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = window.localStorage.getItem(RECENT_CUSTOM_COLORS_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((color: string) => normalizeHexColor(color))
        .filter((color): color is string => Boolean(color) && !isTransparentColor(color))
        .slice(0, 4);
    } catch {
      return [];
    }
  });

  // Avoid flash/mismatch: when user picks a style, keep local state highlighted
  // until the selection lens reflects the same style, then sync and clear.
  const pendingStyleRef = React.useRef<null | 'solid' | 'dashed' | 'dotted' | 'dash-dot'>(null);
  const pendingStrokeWidthRef = React.useRef<number | null>(null);
  const selectionKey = React.useMemo(
    () => [...context.selection].sort().join(','),
    [context.selection],
  );
  const lastSelectionKeyForWidthRef = React.useRef(selectionKey);

  React.useEffect(() => {
    if (lastSelectionKeyForWidthRef.current !== selectionKey) {
      lastSelectionKeyForWidthRef.current = selectionKey;
      pendingStrokeWidthRef.current = null;
    }

    const fromWidth =
      cellBorder?.width ?? imageOutlineLens?.strokeWidth ?? context.selectionStyles?.strokeWidth ?? 2;

    if (pendingStrokeWidthRef.current !== null) {
      if (fromWidth === pendingStrokeWidthRef.current) {
        pendingStrokeWidthRef.current = null;
        setStrokeWidth(fromWidth);
      }
    } else {
      setStrokeWidth(fromWidth);
    }

    const fromContext = resolveStrokeStyleFromDash(
      cellBorder?.dash ?? imageOutlineLens?.dash ?? context.selectionStyles?.dash,
      (cellBorder?.dash?.length ?? 0) > 0 ||
        imageOutlineLens?.dashEnabled ||
        context.selectionStyles?.dashEnabled,
    );
    if (pendingStyleRef.current && pendingStyleRef.current !== fromContext) {
      // User just set a style; wait for context to catch up.
    } else {
      pendingStyleRef.current = null;
      setStrokeStyle(fromContext);
    }
    setStrokeColor(initialStrokeColor);
    const nextCustomColor = normalizeHexColor(initialStrokeColor) ?? '#475569';
    setCustomStrokeColor(nextCustomColor);
    setCustomStrokeColorDraft(nextCustomColor);
  }, [
    cellBorder?.width,
    cellBorder?.dash,
    imageOutlineLens?.strokeWidth,
    imageOutlineLens?.dash,
    imageOutlineLens?.dashEnabled,
    context.selectionStyles?.strokeWidth,
    context.selectionStyles?.dash,
    context.selectionStyles?.dashEnabled,
    initialStrokeColor,
    selectionKey,
  ]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(RECENT_CUSTOM_COLORS_KEY, JSON.stringify(recentCustomColors));
    } catch {
      // ignore storage failures; palette still works in-session
    }
  }, [recentCustomColors]);
  const { mode } = useThemeMode();
  
  const selectedCount = context.selection.size;
  const isDisabled = selectedCount === 0;
  const [showCustomPicker, setShowCustomPicker] = React.useState(false);
  const [isPickingColor, setIsPickingColor] = React.useState(false);
  const colorInputRef = React.useRef<HTMLInputElement>(null);

  // Generate theme-appropriate color palette
  const colorPalette = React.useMemo(() => {
    if (mode === 'dark') {
      return generateDarkModeColorPalette(isOrgSelection ? orgPresetColors : outlinePresetColors);
    }
    return isOrgSelection ? orgPresetColors : outlinePresetColors;
  }, [mode, isOrgSelection]);

  const flattenedPalette = React.useMemo(
    () => colorPalette.flat().map((color) => color.toLowerCase()),
    [colorPalette]
  );

  const paletteColorSet = React.useMemo(
    () => new Set(flattenedPalette),
    [flattenedPalette]
  );

  const addRecentColor = React.useCallback((color: string) => {
    const normalized = normalizeHexColor(color);
    if (!normalized || paletteColorSet.has(normalized.toLowerCase())) {
      return;
    }
    setRecentCustomColors((prev) => {
      const withoutCurrent = prev.filter((entry) => entry !== normalized);
      return [normalized, ...withoutCurrent].slice(0, 4);
    });
  }, [paletteColorSet]);

  const handlePaletteColorClick = React.useCallback((color: string) => {
    if (isTransparentColor(color)) {
      setStrokeColor('transparent');
      return;
    }
    const normalized = normalizeHexColor(color);
    setStrokeColor(normalized ?? color.toUpperCase());
  }, []);

  const handleNativeColorChange = React.useCallback((color: string) => {
    if (isTransparentColor(color)) {
      setStrokeColor('transparent');
      return;
    }
    const normalized = normalizeHexColor(color);
    if (normalized) {
      setStrokeColor(normalized);
      setCustomStrokeColor(normalized);
      setCustomStrokeColorDraft(normalized);
      addRecentColor(normalized);
      return;
    }
    setStrokeColor(color.toUpperCase());
  }, [addRecentColor]);

  const isStrokeColorSelected = React.useCallback((color: string) => {
    if (isTransparentColor(color)) {
      return isTransparentColor(strokeColor);
    }
    const target = normalizeHexColor(color);
    const current = normalizeHexColor(strokeColor);
    if (target && current) {
      return target === current;
    }
    return color.toLowerCase() === strokeColor.toLowerCase();
  }, [strokeColor]);

  const nativeColorInputValue = React.useMemo(() => {
    if (isTransparentColor(strokeColor)) {
      return '#000000';
    }
    return normalizeHexColor(strokeColor) ?? '#475569';
  }, [strokeColor]);

  const isTransparentStroke = React.useMemo(() => isTransparentColor(strokeColor), [strokeColor]);
  const previewBorderColor = React.useMemo(() => {
    if (isTransparentStroke) {
      return 'rgba(148, 163, 184, 0.65)';
    }
    const normalized = normalizeHexColor(strokeColor);
    return normalized ?? strokeColor;
  }, [isTransparentStroke, strokeColor]);

  const quickApply = (opts?: { width?: number; color?: string; style?: string }) => {
    const nextWidth = (typeof opts?.width === 'number') ? opts!.width : strokeWidth;
    const styleKey = (opts?.style ?? strokeStyle);
    const styleConfig = strokeStyles.find(s => s.value === styleKey);
    const rawColor = (opts?.color ?? strokeColor);
    const resolvedColor = isTransparentColor(rawColor)
      ? 'transparent'
      : (normalizeHexColor(rawColor) ?? rawColor.toUpperCase());

    if (!isTransparentColor(resolvedColor)) {
      addRecentColor(resolvedColor);
    }

    if (isOrgSelection) {
      onExecute({ strokeColor: resolvedColor });
      return;
    }

    onExecute({
      strokeWidth: nextWidth,
      strokeColor: resolvedColor,
      dash: styleConfig?.dash || []
    });
  };

  const applyCustomColor = React.useCallback(
    (value: string) => {
      if (!HEX_FULL_REGEX.test(value) && !HEX_SHORT_REGEX.test(value)) return;
      const normalized = expandHex(value);
      setStrokeColor(normalized);
      setCustomStrokeColor(normalized);
      setCustomStrokeColorDraft(normalized);
    },
    []
  );

  const trackRecentColor = React.useCallback((color: string) => {
    if (!color) return;
    setRecentCustomColors(prev => {
      const normalized = color.toLowerCase();
      const withoutCurrent = prev.filter(c => c.toLowerCase() !== normalized);
      return [color, ...withoutCurrent].slice(0, 4);
    });
  }, []);

  const handleColorChange = (color: string) => {
    setStrokeColor(color);
    const normalized = normalizeHexColor(color);
    if (normalized) {
      setCustomStrokeColor(normalized);
      setCustomStrokeColorDraft(normalized);
    }
    quickApply({ color });
    setShowCustomPicker(false);
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const color = e.target.value.toUpperCase();
    applyCustomColor(color);
  };

  const handleCustomColorDraftChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomStrokeColorDraft(e.target.value.toUpperCase());
  };

  const handleCustomColorApply = () => {
    if (!HEX_FULL_REGEX.test(customStrokeColorDraft) && !HEX_SHORT_REGEX.test(customStrokeColorDraft)) {
      return;
    }
    const normalized = expandHex(customStrokeColorDraft);
    applyCustomColor(normalized);
    trackRecentColor(normalized);
    handleColorChange(normalized);
  };

  const canUseEyeDropper = typeof window !== 'undefined' && 'EyeDropper' in window;
  const handleEyeDropperPick = React.useCallback(async () => {
    if (!canUseEyeDropper || isPickingColor) return;
    setIsPickingColor(true);
    try {
      // @ts-expect-error EyeDropper is experimental but supported in modern browsers
      const dropper = new window.EyeDropper();
      const result = await dropper.open();
      if (result?.sRGBHex) {
        applyCustomColor(result.sRGBHex.toUpperCase());
      }
    } catch {
      // swallow cancellation or unsupported errors silently
    } finally {
      setIsPickingColor(false);
    }
  }, [applyCustomColor, canUseEyeDropper, isPickingColor]);

  const isColorSelected = React.useCallback(
    (color: string) => strokeColor.toLowerCase() === color.toLowerCase(),
    [strokeColor]
  );

  const renderColorChip = React.useCallback(
    (color: string, key: string, options?: { trackOnSelect?: boolean }) => {
      const selected = isColorSelected(color);
      const isTransparent = color.toLowerCase() === 'transparent';
      const resolveBorderColor = () => {
        if (isTransparent || !color || color === 'transparent') {
          return 'rgba(148, 163, 184, 0.45)';
        }
        try {
          const expanded = color.startsWith('#') && color.length === 4
            ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
            : color;
          if (expanded.startsWith('#')) {
            const r = parseInt(expanded.slice(1, 3), 16);
            const g = parseInt(expanded.slice(3, 5), 16);
            const b = parseInt(expanded.slice(5, 7), 16);
            const darken = (component: number) => Math.max(component - 40, 0);
            return `rgb(${darken(r)}, ${darken(g)}, ${darken(b)})`;
          }
          return 'rgba(148, 163, 184, 0.45)';
        } catch {
          return 'rgba(148, 163, 184, 0.45)';
        }
      };

      return (
        <button
          key={key}
          type="button"
          data-testid={`tid.tools.shapeOutline.swatch.${isTransparent || !color ? 'transparent' : color.replace('#', '').toLowerCase()}`}
          className="relative flex h-6 w-6 items-center justify-center rounded-full transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
          onClick={() => {
            if (options?.trackOnSelect && !isTransparent) {
              trackRecentColor(color);
            }
            handleColorChange(color);
          }}
          aria-label={`Apply color ${color}`}
          aria-pressed={selected}
        >
          <span className="absolute inset-[1px] rounded-full" />
          <span
            className="relative inline-flex h-full w-full items-center justify-center rounded-full border"
            style={{
              backgroundColor: isTransparent ? '#ffffff' : color,
              borderColor: resolveBorderColor(),
              backgroundImage: isTransparent
                ? 'radial-gradient(circle at 30% 30%, rgba(148, 163, 184, 0.05) 0%, transparent 55%)'
                : undefined
            }}
          >
            {isTransparent && (
              <span className="absolute h-[1px] w-full rotate-45 rounded-full bg-slate-400/70" />
            )}
          </span>
          {selected && (
            <Check className="absolute right-auto bottom-auto h-3 w-3 text-white mix-blend-difference drop-shadow" />
          )}
        </button>
      );
    },
    [handleColorChange, isColorSelected, trackRecentColor]
  );

  const renderAddCustomButton = React.useCallback(
    (key: string) => (
      <button
        key={key}
        type="button"
        data-testid="tid.tools.shapeOutline.custom"
        onClick={() => {
          setShowCustomPicker(true);
        }}
        className="relative flex h-6 w-6 items-center justify-center rounded-full border border-slate-300 bg-white/80 text-slate-400 transition hover:border-slate-400 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
        aria-label={t('properties.pickCustomColor')}
      >
        <Plus className="h-3 w-3" />
      </button>
    ),
    [setShowCustomPicker, t]
  );

  React.useEffect(() => {
    if (!open) {
      setShowCustomPicker(false);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="tid.editor.shapeToolbar.outline"          
          disabled={isDisabled}
          className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t('tools.outline', { defaultValue: 'Outline' })}
        >
          <div className={cn(
            'relative inline-flex w-5 h-5 items-center justify-center rounded-full',
            isTransparentStroke ? 'bg-background border border-dashed border-slate-400/60' : 'bg-transparent'
          )}>
            <div
              className="absolute inset-[2px] rounded-full"
              style={{
                border: `${Math.max(strokeWidth, 1)}px ${strokeStyle === 'solid' ? 'solid' : 'dashed'} ${previewBorderColor}`
              }}
            />
            {isTransparentStroke && (
              <span className="absolute h-[1px] w-full rotate-45 rounded-full bg-slate-400/70" />
            )}
          </div>
        </button>
      </PopoverTrigger>
      {isOrgSelection ? (
        <PopoverContent className="relative w-[168px] p-3 overflow-visible" align="end">
          {/* Label Header */}
          <div className="mb-3 pb-2 border-b border-border">
            <h3 className="text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">{t('tools.borderColor')}</h3>
          </div>

          <div className="space-y-3">
            {/* Preset colors grid */}
            <div className="grid grid-cols-4 gap-2">
              {flattenedPalette.map((color, index) => renderColorChip(color, `palette-${color}-${index}`))}
            </div>

            {/* Recently used custom colors */}
            <div className="grid grid-cols-4 gap-2 pt-1">
              {recentCustomColors.length > 0 ? (
                <>
                  {recentCustomColors.map((color, index) =>
                    renderColorChip(color, `recent-${color}-${index}`, { trackOnSelect: true })
                  )}
                  {renderAddCustomButton('recent-add-button')}
                </>
              ) : (
                renderAddCustomButton('recent-add-empty')
              )}
            </div>
          </div>
          {showCustomPicker && (
            <div
              className="absolute"
              style={{ left: 'calc(100% + 6px)', top: 'calc((3rem + 0.75rem) * 5)' }}
            >
              <div className="flex w-[288px] flex-col gap-3 rounded-[10px] border border-border/60 bg-background p-3 shadow-[0_6px_18px_rgba(2,6,23,0.08)]">
                <AdvancedColorPicker
                  color={customStrokeColor}
                  onColorChange={applyCustomColor}
                />
                <input
                  type="color"
                  value={customStrokeColor}
                  onChange={handleCustomColorChange}
                  ref={colorInputRef}
                  className="absolute h-px w-px opacity-0"
                  tabIndex={-1}
                  aria-hidden="true"
                />
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    data-testid="tid.tools.shapeOutline.customHex"
                    value={customStrokeColorDraft}
                    onChange={handleCustomColorDraftChange}
                    className="h-10 w-[120px] rounded-lg border border-border/60 px-3 text-sm shadow-inner"
                    placeholder="#000000"
                  />
                  <button
                    type="button"
                    data-testid="tid.tools.shapeOutline.eyedropper"
                    onClick={handleEyeDropperPick}
                    className="flex h-10 w-10 min-w-[40px] items-center justify-center rounded-lg border border-border/60 bg-muted/60 transition hover:bg-muted"
                    aria-label={t('properties.pickFromCanvas', 'Pick color from canvas')}
                    disabled={!canUseEyeDropper || isPickingColor}
                  >
                    <Pipette className="h-4 w-4 text-foreground/80" />
                  </button>
                  <Button
                    size="sm"
                    data-testid="tid.tools.shapeOutline.customApply"
                    onClick={handleCustomColorApply}
                    className="h-10 px-4 text-sm font-medium"
                    disabled={!HEX_FULL_REGEX.test(customStrokeColorDraft) && !HEX_SHORT_REGEX.test(customStrokeColorDraft)}
                  >
                    {t('properties.apply', 'Apply')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </PopoverContent>
      ) : (
        <PopoverContent className="w-[280px] p-3" align="end">
        {/* Label Header */}
        <div className="mb-3 pb-2 border-b border-border">
          <h3 className="text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">{t('tools.borderColor')}</h3>
        </div>

        <div className="flex gap-3">
          {/* Left Column - Colors */}
          <div className="space-y-2">
            <div className="grid grid-cols-4 gap-2">
              {colorPalette.flat().map((color, index) => {
                const selected = isStrokeColorSelected(color);
                const isTrans = isTransparentColor(color);
                return (
                  <button
                    key={`${color}-${index}`}
                    type="button"
                    data-testid={`tid.tools.shapeOutline.swatch.${isTrans ? 'transparent' : color.replace('#', '').toLowerCase()}`}
                    className="relative flex h-6 w-6 items-center justify-center rounded-full transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
                    onClick={() => {
                      handlePaletteColorClick(color);
                      if (!isTrans) addRecentColor(color);
                      quickApply({ color });
                    }}
                    aria-label={t('properties.applyColor', { color, defaultValue: 'Apply color {{color}}' })}
                    aria-pressed={selected}
                  >
                    <span className="absolute inset-[1px] rounded-full" />
                    <span
                      className="relative inline-flex h-full w-full items-center justify-center rounded-full border"
                      style={{
                        backgroundColor: isTrans ? '#ffffff' : color,
                        borderColor: isTrans ? 'rgba(148, 163, 184, 0.45)' : (() => {
                          try {
                            const r = parseInt(color.slice(1, 3), 16);
                            const g = parseInt(color.slice(3, 5), 16);
                            const b = parseInt(color.slice(5, 7), 16);
                            const darken = (c: number) => Math.max(c - 40, 0);
                            return `rgb(${darken(r)}, ${darken(g)}, ${darken(b)})`;
                          } catch {
                            return 'rgba(148, 163, 184, 0.45)';
                          }
                        })(),
                        backgroundImage: isTrans
                          ? 'radial-gradient(circle at 30% 30%, rgba(148, 163, 184, 0.05) 0%, transparent 55%)'
                          : undefined
                      }}
                    >
                      {isTrans && (
                        <span className="absolute h-[1px] w-full rotate-45 rounded-full bg-slate-400/70" />
                      )}
                    </span>
                    {selected && (
                      <Check className="absolute right-auto bottom-auto h-3 w-3 text-white mix-blend-difference drop-shadow" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Custom Color Row */}
            <div className="grid grid-cols-4 gap-2 pt-1">
              {recentCustomColors.map((color, index) => {
                const selected = isStrokeColorSelected(color);
                return (
                  <button
                    key={`recent-${color}-${index}`}
                    type="button"
                    data-testid={`tid.tools.shapeOutline.swatch.${color.replace('#', '').toLowerCase()}`}
                    className="relative flex h-6 w-6 items-center justify-center rounded-full transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
                    onClick={() => {
                      handlePaletteColorClick(color);
                      quickApply({ color });
                    }}
                    aria-label={`Apply color ${color}`}
                    aria-pressed={selected}
                  >
                    <span className="absolute inset-[1px] rounded-full" />
                    <span
                      className="relative inline-flex h-full w-full items-center justify-center rounded-full border"
                      style={{
                        backgroundColor: color,
                        borderColor: (() => {
                          try {
                            const r = parseInt(color.slice(1, 3), 16);
                            const g = parseInt(color.slice(3, 5), 16);
                            const b = parseInt(color.slice(5, 7), 16);
                            const darken = (c: number) => Math.max(c - 40, 0);
                            return `rgb(${darken(r)}, ${darken(g)}, ${darken(b)})`;
                          } catch {
                            return 'rgba(148, 163, 184, 0.45)';
                          }
                        })()
                      }}
                    >
                      {selected && (
                        <Check className="absolute right-auto bottom-auto h-3 w-3 text-white mix-blend-difference drop-shadow" />
                      )}
                    </span>
                  </button>
                );
              })}
              <button
                type="button"
                data-testid="tid.tools.shapeOutline.custom"
                onClick={() => document.getElementById('outline-color-picker')?.click()}
                className="relative flex h-6 w-6 items-center justify-center rounded-full border border-slate-300 bg-white/80 text-slate-400 transition hover:border-slate-400 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary"
                aria-label={t('properties.pickCustomColor')}
              >
                <Plus className="h-3 w-3" />
              </button>
              <input
                id="outline-color-picker"
                type="color"
                value={nativeColorInputValue}
                onChange={(e) => {
                  const val = e.target.value;
                  handleNativeColorChange(val);
                  quickApply({ color: val });
                }}
                className="absolute h-px w-px opacity-0"
                tabIndex={-1}
                aria-hidden="true"
              />
            </div>
          </div>

          {!isOrgSelection && (
            <>
              {/* Vertical Separator */}
              <Separator orientation="vertical" className="h-auto" />

              {/* Right Column - Thickness & Style */}
              <div className="space-y-3 flex-1">
                {/* Border Thickness */}
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-medium text-muted-foreground">{t('connector.thickness')}</Label>
                  <div className="grid grid-cols-4 gap-1">
                    {[0, 1, 2, 3, 4, 5, 6, 8].map((width) => {
                      const isSelected = strokeWidth === width;
                      return (
                        <button
                          key={width}
                          type="button"
                          data-testid={`tid.tools.shapeOutline.width.${width}`}
                          onClick={() => {
                            pendingStrokeWidthRef.current = width;
                            setStrokeWidth(width);
                            quickApply({ width });
                          }}
                          className={`flex h-7 items-center justify-center rounded transition-all ${
                            isSelected
                              ? 'bg-primary'
                              : 'hover:bg-muted'
                          }`}
                          aria-label={t('properties.borderWidthPixels', { width, defaultValue: 'Border width {{width}}px' })}
                        >
                          <div
                            className="rounded-full bg-foreground"
                            style={{
                              width: width === 0 ? '4px' : `${Math.min(width * 1.5 + 3, 12)}px`,
                              height: width === 0 ? '4px' : `${Math.min(width * 1.5 + 3, 12)}px`,
                              opacity: width === 0 ? 0.3 : 1
                            }}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Border Style */}
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-medium text-muted-foreground">{t('connector.style')}</Label>
                  <div className="grid grid-cols-2 gap-1">
                    <TooltipProvider>
                      {strokeStyles.slice(0, 4).map((style) => (
                        <Tooltip key={style.value} delayDuration={150}>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              data-testid={`tid.tools.shapeOutline.style.${style.value}`}
                              className={`h-7 px-2 rounded flex items-center justify-center transition-all ${
                                strokeStyle === style.value
                                  ? 'bg-primary text-primary-foreground'
                                  : 'hover:bg-muted'
                              }`}
                              onClick={() => {
                                const v = style.value as 'solid' | 'dashed' | 'dotted' | 'dash-dot';
                                pendingStyleRef.current = v;
                                setStrokeStyle(v);
                                quickApply({ style: v });
                              }}
                              aria-label={t(`connector.${style.value}`, style.label)}
                            >
                              <svg width="28" height="3" viewBox="0 0 28 3" className="text-current">
                                {style.value === 'solid' && (
                                  <line x1="0" y1="1.5" x2="28" y2="1.5" stroke="currentColor" strokeWidth="2" />
                                )}
                                {style.value === 'dashed' && (
                                  <line x1="0" y1="1.5" x2="28" y2="1.5" stroke="currentColor" strokeWidth="2" strokeDasharray="5 2" />
                                )}
                                {style.value === 'dotted' && (
                                  <line x1="0" y1="1.5" x2="28" y2="1.5" stroke="currentColor" strokeWidth="2" strokeDasharray="1 2" />
                                )}
                                {style.value === 'dash-dot' && (
                                  <line x1="0" y1="1.5" x2="28" y2="1.5" stroke="currentColor" strokeWidth="2" strokeDasharray="5 2 1 2" />
                                )}
                              </svg>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            {style.label}
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </TooltipProvider>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </PopoverContent>
      )}
    </Popover>
  );
};

export class ShapeOutlineTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'shape-outline',
    name: 'tools.outline',
    icon: 'border',
    category: 'style',
    description: 'Change outline style of selected shapes',
    version: '1.0.0',
    applicability: TOOL_APPLICABILITY.ALL_SHAPES,
    
    requiresSelection: true,
    minSelection: 1,
    
    ui: {
      toolbar: true,
      contextMenu: true,
      propertyPanel: true,
      sidebar: false,
      floatingPanel: true,
      statusBar: false
    },
    
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true
    }
  };

  canExecute(context: IToolContext): boolean {
    return super.canExecute(context) && context.selection.size > 0;
  }

  execute(params?: { strokeWidth?: number; strokeColor?: string; dash?: number[] }): ICommand | null {
    if (!this.context || this.context.selection.size === 0 || !params) {
      return null;
    }

    const selectedIds = Array.from(this.context.selection);
    const selectionState = useTableCellStore.getState().selections;
    const tableCellKeysByNodeId: Record<string, TableCellKey[]> = {};
    const tableNodeIds: string[] = [];
    const remainingNodeIds: string[] = [];
    const orgNodeIds: string[] = [];
    const nonOrgNodeIds: string[] = [];
    const imageNodeIds: string[] = [];

    selectedIds.forEach((nodeId) => {
      const keys = selectionState[nodeId]?.selectedKeys ?? [];
      if (keys.length > 0) {
        tableNodeIds.push(nodeId);
        tableCellKeysByNodeId[nodeId] = keys;
        return;
      }
      remainingNodeIds.push(nodeId);
    });

    remainingNodeIds.forEach((nodeId) => {
      if (isImageNode(this.context!, nodeId)) {
        imageNodeIds.push(nodeId);
        return;
      }
      if (isOrgChartNode(this.context!, nodeId)) {
        orgNodeIds.push(nodeId);
        return;
      }
      nonOrgNodeIds.push(nodeId);
    });

    const commands: ICommand[] = [];

    if (tableNodeIds.length > 0) {
      commands.push(new UpdateTableCellStylesCommand({
        nodeIds: tableNodeIds,
        cellKeysByNodeId: tableCellKeysByNodeId,
        stylePatch: {
          borderColor: params.strokeColor,
          borderWidth: params.strokeWidth,
          borderDash: params.dash,
        },
      }));
    }
    
    const standardNodeIds = [...nonOrgNodeIds, ...orgNodeIds];
    if (standardNodeIds.length > 0) {
      const dash = Array.isArray(params.dash) ? params.dash : undefined;
      const stylePatch: Record<string, unknown> = {
        ...(params.strokeWidth !== undefined ? { strokeWidth: params.strokeWidth } : {}),
        ...(params.strokeColor ? { strokeColor: params.strokeColor } : {}),
        ...(dash ? { dash, dashEnabled: dash.length > 0 } : {}),
      };

      if (Object.keys(stylePatch).length > 0) {
        commands.push(new ChangeNodeStyleCommand(standardNodeIds, 'stylePatch', stylePatch));
      }
    }

    if (imageNodeIds.length > 0) {
      const isTransparentStroke = isTransparentColor(params.strokeColor);
      const dash = Array.isArray(params.dash) ? params.dash : [];
      const dashEnabled = dash.length > 0;
      const strokeWidth = typeof params.strokeWidth === 'number' ? params.strokeWidth : 1;
      const imageUpdates: ImageNodeUpdate[] = imageNodeIds.map((nodeId) => ({
        nodeId,
        changes: {
          attrs: {
            borderEnabled: !isTransparentStroke && strokeWidth > 0,
            borderWidth: Math.max(0, Number(strokeWidth)),
            borderDash: dash,
            dash,
            dashEnabled,
            ...(isTransparentStroke ? {} : { borderColor: params.strokeColor }),
          },
        },
      }));
      commands.push(new UpdateImageNodeAttrsCommand(imageUpdates));
    }

    // Track styling applied (aggregated)
    if (commands.length > 0) {
      SessionMetrics.increment('styling_applied');
    }

    return commands.length > 0 ? new CompositeCommand('Change Outline', commands) : null;
  }

  getToolbarComponent() {
    return ShapeOutlineToolComponent;
  }

  getFloatingPanelComponent() {
    return ShapeOutlineToolComponent;
  }
}
